const GUIDE_DRAWER_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function ensureGuideInertElems() {
  if (!window.inertElems?.length) {
    window.inertElems = document.querySelectorAll('[data-js-inert]');
  }
}

function lockGuidePageInert() {
  ensureGuideInertElems();
  window.inertElems.forEach((elm) => elm.setAttribute('inert', ''));
}

function unlockGuidePageInert() {
  if (
    (window.openedSidebars?.length ?? 0) > 0 ||
    document.querySelector('custom-guide.active') ||
    document.querySelector('product-modal.active') ||
    document.querySelector('custom-shop-the-look.active')
  ) {
    return;
  }

  ensureGuideInertElems();
  window.inertElems.forEach((elm) => elm.removeAttribute('inert'));
}

function getGuideDrawerFocusable(container) {
  if (!container) return [];

  return [...container.querySelectorAll(GUIDE_DRAWER_FOCUSABLE)].filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      !el.closest('[inert], [aria-hidden="true"]') &&
      el.getClientRects().length > 0
  );
}

function trapTabInGuideDrawer(event, container) {
  if (event.key !== 'Tab') return false;

  const focusable = getGuideDrawerFocusable(container);
  if (!focusable.length) return false;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const activeIndex = focusable.indexOf(document.activeElement);

  if (activeIndex === -1) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return true;
  }

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return true;
  }

  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
    return true;
  }

  return false;
}

function focusFirstInGuideDrawer(container) {
  const title = container?.querySelector("#custom-guide-title");
  if (title?.textContent?.trim()) {
    title.focus({ preventScroll: true });
    return;
  }

  const focusable = getGuideDrawerFocusable(container);
  (focusable[0] || container)?.focus({ preventScroll: true });
}

// Core guide logic as a regular class
class CustomGuideCore {
  static CACHE = new Map();
  static CACHE_LIMIT = 10;

  constructor() {
    this.popup = document.querySelector("custom-guide");
    this.loader = this.popup?.querySelector("custom-guide-loader");
    this.close = this.popup?.querySelector("[data-js-close]");
    this.content = this.popup?.querySelector("custom-guide-content");
    this.wrapper = this.popup?.querySelector("custom-guide-wrapper");
    this.titleEl = this.popup?.querySelector("#custom-guide-title");
    this.lastTrigger = null;
    this._onKeyDown = this._onKeyDown.bind(this);
    document.addEventListener("keydown", this._onKeyDown);

    if (this.popup && this.close) {
      this.popup.addEventListener("click", (e) => {
        if (e.target === this.popup && e.target.classList.contains("active"))
          this.hide();
      });
      this.close.addEventListener("click", () => this.hide());
    }

  }

  normalizeGuideUrl(url) {
    if (!url || typeof url !== "string") return "";

    const trimmed = url.trim();
    if (!trimmed) return "";

    try {
      if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
        const parsed = new URL(trimmed);
        return `${parsed.pathname}${parsed.search}`;
      }
    } catch {
      return "";
    }

    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  open(url, sectionId = "helper-page-content", triggerElement = null) {
    const normalizedUrl = this.normalizeGuideUrl(url);
    if (!normalizedUrl || !this.popup) return;

    this.lastTrigger = triggerElement;
    this.sectionId = sectionId;
    this.url = `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}section_id=${sectionId}`;
    this.setProvisionalTitle(triggerElement);
    this.show();
  }

  cacheContent(u, c) {
    CustomGuideCore.CACHE.set(u, c);
    if (CustomGuideCore.CACHE.size > CustomGuideCore.CACHE_LIMIT)
      CustomGuideCore.CACHE.delete(CustomGuideCore.CACHE.keys().next().value);
  }

  getCachedContent(u) {
    return CustomGuideCore.CACHE.get(u) || null;
  }

  setProvisionalTitle(triggerElement) {
    const triggerLabel = triggerElement?.textContent?.trim();
    if (!triggerLabel || !this.titleEl) return;

    this.titleEl.textContent = triggerLabel;
  }

  show() {
    if (!this.popup) return;

    this.popup.classList.remove("closing");
    this.popup.classList.add("active");
    this.wrapper?.setAttribute("aria-hidden", "false");
    document.body.classList.add("modal-open", "guide-dialog-active");
    lockGuidePageInert();
    [this.popup, this.loader].forEach((el) => el?.classList.add("working"));
    this.fetchContent(this.url);
  }

  hide() {
    if (!this.popup) return;

    const trigger = this.lastTrigger;
    this.lastTrigger = null;

    this.popup.classList.remove("active");
    this.popup.classList.add("closing");
    this.popup.classList.remove("loaded");
    this.wrapper?.classList.remove("sidebar--opened");
    this.wrapper?.setAttribute("aria-hidden", "true");
    unlockGuidePageInert();
    document.body.classList.remove("guide-dialog-active");
    if (
      !document.querySelector("custom-shop-the-look.active") &&
      !document.querySelector("product-modal.active")
    ) {
      document.body.classList.remove("modal-open");
    }

    if (this.titleEl) {
      this.titleEl.textContent = "";
    }

    trigger?.focus?.({ preventScroll: true });

    setTimeout(() => {
      this.popup?.classList.remove("closing", "working");
    }, 300);
  }

  _onKeyDown(e) {
    if (!this.popup?.classList.contains("active")) return;
    if (this.wrapper?.getAttribute("aria-hidden") === "true") return;

    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
      return;
    }

    trapTabInGuideDrawer(e, this.wrapper);
  }

  async fetchContent(u) {
    let txt = this.getCachedContent(u);
    if (txt) {
      this.renderContent(txt);
      return this._done();
    }
    try {
      const res = await fetch(u);
      txt = await res.text();
      this.cacheContent(u, txt);
      this.renderContent(txt);
    } catch (e) {
      console.error("Error fetching content:", e);
    }
    this._done();
  }

  renderContent(t) {
    if (this.content) this.content.innerHTML = t;
  }

  setTitle() {
    const title = this.content?.querySelector("[data-guide-title]");
    if (title?.textContent?.trim() && this.titleEl) {
      this.titleEl.textContent = title.textContent.trim();
    }
  }

  _done() {
    [this.popup, this.loader].forEach((el) => el?.classList.remove("working"));
    this.sectionId = null;
    this.setTitle();

    requestAnimationFrame(() => {
      this.popup?.classList.add("loaded");
      requestAnimationFrame(() => {
        this.wrapper?.classList.add("sidebar--opened");
        focusFirstInGuideDrawer(this.wrapper);
      });
    });
    this.content
      ?.querySelectorAll(".rte a img")
      .forEach((i) => (i.parentNode.style.border = "none"));
    this.content
      ?.querySelectorAll(".rte table")
      .forEach(
        (t) => (t.outerHTML = `<div class="table-wrapper">${t.outerHTML}</div>`)
      );

    // Initialize table hover functionality for newly loaded content
    if (this.content?.querySelector("table")) {
      this.initializeTableHovers();
    }
    // Initialize unit conversion for tables
    // this.initializeUnitConversion();
  }

  initializeTableHovers() {
    const tables = this.content.querySelectorAll("table");

    tables.forEach((table) => {
      const cells = table.querySelectorAll("td");

      cells.forEach((cell) => {
        cell.addEventListener("mouseenter", function () {
          // Remove any existing hover classes from this table
          cells.forEach((c) => {
            c.classList.remove("hover-row", "hover-col", "hover-cell");
          });

          // Get current cell position
          const row = this.parentElement;
          const cellIndex = Array.from(row.children).indexOf(this);

          // Highlight current row
          row.querySelectorAll("td").forEach((td) => {
            td.classList.add("hover-row");
          });

          // Highlight current column
          table
            .querySelectorAll(`tr td:nth-child(${cellIndex + 1})`)
            .forEach((td) => {
              td.classList.add("hover-col");
            });

          // Highlight current cell with stronger color
          this.classList.add("hover-cell");
        });

        cell.addEventListener("mouseleave", function () {
          cells.forEach((c) => {
            c.classList.remove("hover-row", "hover-col", "hover-cell");
          });
        });
      });
    });
  }
}

// HTML element wrapper that uses CustomGuideCore
if (!customElements.get("jack-custom-guide")) {
  class JackCustomGuide extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.addEventListener("click", (e) => {
        const link = e.target.closest("a");
        if (link) e.preventDefault();

        const sectionId = this.dataset.sectionId || "helper-page-content";
        const url = this.dataset.url;
        const trigger = link || this;

        const openGuide = () => {
          window.CustomGuideOpener.open(url, sectionId, trigger);
        };

        if (window.CustomGuideOpener) {
          openGuide();
        } else {
          window.addEventListener("customGuideReady", openGuide, { once: true });
        }
      });
    }
  }

  customElements.define("jack-custom-guide", JackCustomGuide);
}

// Initialize CustomGuideCore when popup is available
function initCustomGuideOpener() {
  if (!document.querySelector("custom-guide")) return;
  window.CustomGuideOpener = new CustomGuideCore();
  window.dispatchEvent(new Event("customGuideReady"));
}

initCustomGuideOpener();
